import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchOAuthRequest, approveOAuthRequest, denyOAuthRequest } from '../services/oauthService';
import Button from '../components/Button';
import './OAuthAuthorizePage.css';

/**
 * Consent screen for the MCP OAuth flow. The API's /authorize endpoint parks
 * the client's request and sends the browser here with ?request=<id>; the
 * signed-in engineer approves or denies, and the browser follows the
 * redirect the API returns, back to the client with a code (or an error).
 */
const OAuthAuthorizePage = () => {
  const [params] = useSearchParams();
  const requestId = params.get('request');
  const [state, setState] = useState({ loading: true, request: null, account: null, error: null, busy: false });

  useEffect(() => {
    let cancelled = false;
    if (!requestId) {
      setState({ loading: false, request: null, account: null, error: 'Missing authorization request. Start again from the client that asked you to sign in.', busy: false });
      return () => {};
    }
    fetchOAuthRequest(requestId)
      .then((data) => { if (!cancelled) setState({ loading: false, request: data.request, account: data.account, error: null, busy: false }); })
      .catch((e) => {
        if (cancelled) return;
        const message = e.status === 403
          ? 'REKKIE MCP is available to @rekkie.com accounts. Sign out and sign in with your work account.'
          : e.status === 404 ? 'This authorization request has expired or was already used. Start again from the client.' : e.message;
        setState({ loading: false, request: null, account: null, error: message, busy: false });
      });
    return () => { cancelled = true; };
  }, [requestId]);

  const decide = async (approve) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { redirectUrl } = approve ? await approveOAuthRequest(requestId) : await denyOAuthRequest(requestId);
      window.location.assign(redirectUrl);
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: e.message }));
    }
  };

  const { loading, request, account, error, busy } = state;
  const clientLabel = request?.clientName || request?.clientId || 'An MCP client';
  let redirectHost = null;
  try { redirectHost = request ? new URL(request.redirectUri).host : null; } catch (e) { redirectHost = null; }

  return (
    <div className="oauth-page">
      <div className="oauth-card">
        <div className="oauth-eyebrow">REKKIE MCP</div>
        {loading && <p className="oauth-muted">Loading authorization request…</p>}
        {!loading && error && <div className="oauth-error">{error}</div>}
        {!loading && request && (
          <>
            <h2>Allow {clientLabel} to use REKKIE MCP as you?</h2>
            <p className="oauth-muted">
              It will read per-user analytics, crashes, ski days and the release register as <strong>{account?.email}</strong>.
              Everything it can do is read-only.
            </p>
            <dl className="oauth-details">
              <dt>Client</dt><dd>{clientLabel}{request.clientUri ? <> · <a href={request.clientUri} target="_blank" rel="noreferrer">{request.clientUri}</a></> : null}</dd>
              <dt>Returns to</dt><dd><code>{redirectHost || request.redirectUri}</code></dd>
              <dt>Access</dt><dd>{(request.scopes || []).join(', ') || 'mcp'} (read-only tools and resources)</dd>
            </dl>
            <div className="oauth-actions">
              <Button variant="primary" onClick={() => decide(true)} disabled={busy}>Allow</Button>
              <Button variant="outline" onClick={() => decide(false)} disabled={busy}>Deny</Button>
            </div>
            <p className="oauth-muted oauth-fine">You can revoke access later by revoking the client's token from the client, or by asking an admin. Tokens expire after an hour and renew for up to 30 days.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthAuthorizePage;
